import { Button } from '../../components/Button';
import { ColoredScrollText } from '../../icons/custom';
import { ListStart, ScanText } from '../../icons/lucide';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { brandColor } from '../../styles/brand-colors';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import type { NotesGenerateRowProps } from './NotesGenerateRow.types';

/** Figma draws the glyphs at 16 inside the `m` button. */
const ICON_SIZE = 16;

/**
 * "Generate from your document:" — the notes editor's get-started row.
 *
 * Three DS `secondary` buttons that seed an empty note from the deck's source:
 * full notes, a summary, or a concept list. Both platforms render THIS: web
 * mounts it inside the Notion-like editor, the phone floats it over its notes
 * WebView. It exists because they had drifted completely — mobile was three
 * raw `<button>`s with hand-written CSS (flat fill, 12px radius, no bevel, no
 * icons) sitting next to web's DS buttons.
 *
 * The glyphs come in via `leftSlot`, not `leftIcon`: each one carries its own
 * brand colour (blue / amber / teal) and `leftIcon` would recolour it to the
 * variant's foreground. `brandColor` resolves the two lucide ones per theme —
 * a Tailwind class never reaches `react-native-svg`'s `stroke`.
 *
 * Labels arrive pre-translated (the DS carries no i18n).
 */
export function NotesGenerateRow({
  labels,
  onGenerate,
  disabled = false,
  className,
  buttonsClassName,
  testID,
}: NotesGenerateRowProps) {
  const { isDark } = useTheme();

  return (
    <View className={cn('flex-col gap-2', className)} testID={testID}>
      {/* `px-4` lines the hint up with the first button's label rather than its
          border, which is where the eye starts reading the stack — so it stays
          START-aligned. Do not centre it: the block shrink-wraps its buttons, so
          the caller's own centring already places the whole stack, and hardcoding
          `text-center` here silently re-aligned web's row (which had always read
          left) and drifts further once `buttonsClassName` flips the buttons to a
          wide row. */}
      <Text className="px-4 text-b2 text-secondary-400">{labels.hint}</Text>
      <View className={cn('flex-col gap-2', buttonsClassName)}>
        <Button
          variant="secondary"
          size="m"
          leftSlot={<ColoredScrollText size={ICON_SIZE} />}
          disabled={disabled}
          testID={subTestID(testID, 'notes')}
          onPress={() => onGenerate('notes')}
          webProps={{ type: 'button' }}>
          {labels.notes}
        </Button>
        <Button
          variant="secondary"
          size="m"
          leftSlot={
            <ScanText size={ICON_SIZE} color={brandColor('amber', '800', isDark)} />
          }
          disabled={disabled}
          testID={subTestID(testID, 'summary')}
          onPress={() => onGenerate('summary')}
          webProps={{ type: 'button' }}>
          {labels.summary}
        </Button>
        <Button
          variant="secondary"
          size="m"
          leftSlot={
            <ListStart size={ICON_SIZE} color={brandColor('teal', '800', isDark)} />
          }
          disabled={disabled}
          testID={subTestID(testID, 'concepts')}
          onPress={() => onGenerate('concepts')}
          webProps={{ type: 'button' }}>
          {labels.concepts}
        </Button>
      </View>
    </View>
  );
}
(NotesGenerateRow as { displayName?: string }).displayName = 'NotesGenerateRow';
